import { Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { PhotoRoomLabels } from "./PhotoRoomLabels";
import { formatPhotoLabelList, getPhotoLabels } from "./photoLabels.utils";
import type { PhotoGalleryItem } from "./photos.types";
import { getPhotoImageUrl } from "./photos.utils";

interface PhotoGalleryTileProps {
  photo: PhotoGalleryItem;
  altText: string;
  isSelected: boolean;
  isSelectionActive: boolean;
  onSelect: (photo: PhotoGalleryItem) => void;
  onToggleSelect?: (photo: PhotoGalleryItem) => void;
  timestampLabel?: string;
}

export function PhotoGalleryTile({
  photo,
  altText,
  isSelected,
  isSelectionActive,
  onSelect,
  onToggleSelect,
  timestampLabel,
}: PhotoGalleryTileProps) {
  const imageUrl = getPhotoImageUrl(photo, true);
  const [loadedImageUrl, setLoadedImageUrl] = useState<string>();
  const isImageLoading = loadedImageUrl !== imageUrl;
  const labels = getPhotoLabels(photo);

  return (
    <div
      className={`group relative m-1 flex flex-col items-center rounded-sm p-1 ${isSelected ? "ring-2 ring-indigo-600" : ""}`}
      title={labels.length > 0 ? formatPhotoLabelList(labels) : undefined}
    >
      <button
        type="button"
        aria-label={isSelectionActive ? (isSelected ? `Deselect ${altText}` : `Select ${altText}`) : `View ${altText}`}
        onClick={() => {
          if (isSelectionActive && onToggleSelect) {
            onToggleSelect(photo);
            return;
          }
          onSelect(photo);
        }}
        className="relative flex items-center justify-center rounded-sm border-0 !bg-[#000] p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
      >
        <img
          key={imageUrl}
          loading="lazy"
          decoding="async"
          alt={altText}
          className="h-[200px] w-[200px] cursor-pointer rounded-sm object-contain"
          src={imageUrl}
          onLoad={() => setLoadedImageUrl(imageUrl)}
          onError={() => setLoadedImageUrl(imageUrl)}
        />
        {isImageLoading && (
          <span aria-label={`Loading ${altText}`} className="absolute inset-0 flex items-center justify-center bg-black/20">
            <Loader2 className="h-6 w-6 animate-spin text-white" aria-hidden="true" />
          </span>
        )}
      </button>
      {timestampLabel && <span className="w-[200px] pt-1 text-xs text-gray-500">{timestampLabel}</span>}
      <PhotoRoomLabels roomPath={photo.roomPath} collapse className="flex w-[200px] pt-1.5" />
      {labels.length > 0 && (
        <ul aria-label="Photo labels" className="flex w-[200px] flex-wrap justify-start gap-1 pt-1.5">
          {labels.map((label) => (
            <li key={label.id}>
              <Badge variant="secondary" className="max-w-[200px] truncate">
                {label.name}
              </Badge>
            </li>
          ))}
        </ul>
      )}
      {onToggleSelect && (
        <button
          type="button"
          aria-label={isSelected ? "Deselect photo" : "Select photo"}
          onClick={(event) => {
            event.stopPropagation();
            onToggleSelect(photo);
          }}
          className={`absolute top-2 left-2 z-10 flex h-11 w-11 items-center justify-center rounded border-0 bg-black/50 p-0 transition-opacity ${
            isSelectionActive ? "opacity-100" : "opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 focus-visible:opacity-100"
          }`}
        >
          <span
            className={`flex h-5 w-5 items-center justify-center rounded border-2 ${
              isSelected ? "border-indigo-600 bg-indigo-600" : "border-white bg-transparent"
            }`}
          >
            {isSelected && <Check className="h-3 w-3 text-white" />}
          </span>
        </button>
      )}
    </div>
  );
}
